import React from 'react';
import {
  Text,
  TSpan,
} from 'react-native-svg';
import { mat2d, vec2 } from 'utility/gl-matrix';
import Proto from 'generated/freeform_pb';
import { ObjectType } from 'generated/freeform_v2_pb';
import unwrap from 'utility/unwrap';
import AssetView from 'components/container/ConnectedAssetView';
import ProportionalRoundedCorners from 'components/ProportionalRoundedCorners';
import { protoAffineTransformToMat2d } from 'utility/protoconversion';
import { Avatar } from 'components/AvatarStack';
import * as API from 'generated/api';
import { extractText as extractTextFromFFObject } from 'model/FFObject';
import styles from './ObjectContentView.module.css';

interface ConditionalWrapper$Props {
  shouldWrap: boolean;
  wrap: (children: JSX.Element) => JSX.Element;
  children: JSX.Element;
}

function ConditionalWrapper({ shouldWrap, wrap, children }: ConditionalWrapper$Props) {
  return shouldWrap ? wrap(children) : children;
}

interface FontSpec {
  fontFamily: string;
  fontWeight?: number;
}

function fontNameToDOMFontFamily(fontName: string): FontSpec {
  const mapping: Record<string, string | FontSpec> = {
    'Menlo-Regular': 'Menlo',
    'Rockwell-Regular': 'Rockwell',
    'DonegalOne-Regular': 'Donegal One',
    'JosefinSans-Regular': 'Josefin Sans',
    'LaBelleAurore': 'La Belle Aurore',
    'VampiroOne-Regular': 'Vampiro One',
    'Atma-Medium': 'Atma',
    'TimesNewRomanPSMT': 'Times New Roman',
    'AvenirNextRoundedPro-Med': 'Avenir Next',
    'RockSalt-Regular': 'Rock Salt',
    'NovaCut': 'Nova Cut',
    'AmericanTypewriter': 'American Typewriter',
    'HoeflerText-Black': {
      fontFamily: 'Hoefler Text',
      fontWeight: 700,
    },
  }

  const mapped = mapping[fontName];
  if (mapped != null) {
    if (typeof mapped === 'string') {
      return {fontFamily: mapped};
    } else {
      return mapped;
    }
  }
  return {fontFamily: fontName};
}

function rgbaToCSS(rgba: {red: number, green: number, blue: number, alpha: number}): string {
  return `rgba(${[
    ...[rgba.red, rgba.green, rgba.blue].map((n) => n * 256),
    rgba.alpha
  ].join(', ')})`;
}

interface Props {
  object: Proto.FFObject;
  creator: API.MinimalUser;
}

function ObjectContentView(props: Props) {
  const size = vec2.fromSize(props.object.getSize()?.toObject() || {width: 0, height: 0});

  switch (props.object.getType()) {
    case ObjectType.TEXT:
      const isNewTextContent = props.object.getNewText() != null;

      const NewTextContent = () => {
        const textContent = unwrap(props.object.getNewText()?.toObject());
        const runsList = textContent.runsList;
        if (runsList == null || runsList.length === 0) {
          return <Text>&nbsp;</Text>;
        }
        return (
          <>
            {(() => {
              const senderTag = textContent.senderTag;
              if (senderTag == null) {
                return null;
              }
              return (
                <Avatar
                  user={props.creator}
                  center={[senderTag.x, senderTag.y]}
                  diameter={senderTag.diameter}
                />
              );
            })()}
          <Text>
            {
              runsList.map((run, index) => {
                const style = unwrap(run.style);
                return (
                  <TSpan
                    key={index}
                    fill={rgbaToCSS(unwrap(style.color))}
                    {...fontNameToDOMFontFamily(style.fontName)}
                    fontSize={style.fontSize}
                    textAnchor="start"
                    x={run.left}
                    y={run.base}
                    {
                      // DOM-only
                      // Preserve whitespace in text
                      ...({
                        style: {
                          whiteSpace: 'pre',
                        },
                      } as any)
                    }
                  >
                    {run.text.length === 0 ? '\u00A0' : run.text}
                  </TSpan>
                );
              })
            }
          </Text>
          </>
        );
      };

      const OldTextContent = () => {
        const sectionsList = unwrap(props.object.getText()?.toObject().sectionsList);
        if (sectionsList.length === 0) {
          return <Text>&nbsp;</Text>;
        }

        return (
          <Text>
            {
              sectionsList.map((run, index) => {
                const style = unwrap(run.style);
                return (
                  <TSpan
                    key={index}
                    fill={rgbaToCSS(unwrap(style.textcolor))}
                    {...fontNameToDOMFontFamily(style.fontname)}
                    fontSize={style.fontsize}
                    textAnchor="start"
                    x={style.fontsize}
                    y={style.fontsize + 12 /* eyeballed */}
                  >
                    {run.text.length === 0 ? '\u00A0' : run.text}
                  </TSpan>
                );
              })
            }
          </Text>
        );
      };

      const backgroundColor =
        isNewTextContent
        ? unwrap(props.object.getNewText()).getBubble()?.getColor()?.toObject()
        : unwrap(props.object.getText()).getBackgroundcolor()?.toObject();

      const cornerRadius =
        isNewTextContent
        ? unwrap(props.object.getNewText()).getBubble()?.getCornerRadius()
        : 24;

      const linkURL = (() => {
        const text = extractTextFromFFObject(props.object);
        if (text == null) {
          return null;
        }

        const urlRegex = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/;
        const match = urlRegex.exec(text);
        return match == null ? null : match[0];
      })();

      return (
        <ConditionalWrapper
          shouldWrap={linkURL != null}
          wrap={(children) => <a className={styles.link} href={linkURL ?? ''}>{children}</a>}
        >
          <ProportionalRoundedCorners
            color={backgroundColor == null ? null : rgbaToCSS(backgroundColor)}
            cornerRadius={cornerRadius == null ? null : cornerRadius}
            appliedTransform={protoAffineTransformToMat2d(unwrap(props.object.getTransform()), mat2d.create())}
            size={size}
          >
            {isNewTextContent ? NewTextContent() : OldTextContent()}
          </ProportionalRoundedCorners>
        </ConditionalWrapper>
      );

    case ObjectType.PHOTO:
    case ObjectType.GIF:
    case ObjectType.DRAWING:
      const asset = props.object.getAsset();
      const uuid = asset?.getUuid();
      if (uuid == null) {
        // todo
        return null;
      }
      return (
        <AssetView
          uuid={uuid}
          size={size}
        />
      );

    default:
      // TODO
      return null;
  }
}

export default ObjectContentView;
